import type { SuperAdminUserSummary } from "@multi-chat/shared";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { Badge } from "../../components/ui/badge/index.js";
import { Button } from "../../components/ui/button/index.js";
import { ProductErrorState } from "../../components/common/ProductErrorState.js";
import { LtrValue } from "../../components/common/LtrValue.js";
import type { SuperAdminLoadState } from "./SuperAdminDashboardPage.js";
import { SuperAdminCursorPaginationBar } from "./SuperAdminCursorPaginationBar.js";
import {
  contactsTableHeaderLabelClass,
  superAdminTableBodyCellClass,
  superAdminTableBodyRowClass,
  superAdminTableClass,
  superAdminTableHeaderCellClass,
  superAdminTableHeadRowClass,
  superAdminTableScrollClass,
  superAdminTableWorkspaceClass,
  type SuperAdminTablePageSize,
} from "./super-admin-table-presentation.js";

export function SuperAdminUserStatusBadge({ status }: { status: "ACTIVE" | "DISABLED" }): React.JSX.Element {
  const { t } = useTranslation();
  return (
    <Badge variant={status === "ACTIVE" ? "success" : "default"}>{t(`superAdmin.users.status.${status}`)}</Badge>
  );
}

function HeaderCell({ children }: { children: React.ReactNode }): React.JSX.Element {
  return (
    <th className={superAdminTableHeaderCellClass}>
      <span className={contactsTableHeaderLabelClass}>{children}</span>
    </th>
  );
}

export function SuperAdminUsersView({
  state,
  search,
  pageSize,
  isFirstPage,
  onSearchChange,
  onSearchSubmit,
  onPageSizeChange,
  onNextPage,
  onFirstPage,
  onRetry,
}: {
  state: SuperAdminLoadState<{ items: SuperAdminUserSummary[]; nextCursor: string | null }>;
  search: string;
  pageSize: SuperAdminTablePageSize;
  isFirstPage: boolean;
  onSearchChange: (value: string) => void;
  onSearchSubmit: () => void;
  onPageSizeChange: (size: SuperAdminTablePageSize) => void;
  onNextPage: () => void;
  onFirstPage: () => void;
  onRetry: () => void;
}): React.JSX.Element {
  const { t, i18n } = useTranslation();
  const numberFormat = new Intl.NumberFormat(i18n.language);
  const ready = state.status === "ready";
  const items = ready ? state.data.items : [];
  const nextCursor = ready ? state.data.nextCursor : null;

  return (
    <div className="grid gap-4" data-testid="super-admin-users">
      <div>
        <h1 className="m-0 text-xl font-semibold text-foreground">{t("superAdmin.users.title")}</h1>
        <p className="m-0 mt-1 text-sm text-muted">{t("superAdmin.users.lead")}</p>
      </div>
      <form
        className="flex flex-wrap items-center gap-2"
        onSubmit={(event) => {
          event.preventDefault();
          onSearchSubmit();
        }}
      >
        <label className="sr-only" htmlFor="super-admin-user-search">
          {t("superAdmin.users.searchLabel")}
        </label>
        <input
          id="super-admin-user-search"
          type="search"
          dir="ltr"
          value={search}
          onChange={(event) => onSearchChange(event.target.value)}
          placeholder={t("superAdmin.users.searchPlaceholder")}
          className="h-9 w-full max-w-sm rounded-md border border-line/70 bg-surface px-3 text-sm text-foreground shadow-[inset_0_1px_0_rgba(255,255,255,0.55)]"
          data-testid="super-admin-user-search"
        />
        <Button type="submit" size="sm" variant="outline">
          {t("superAdmin.organizations.search")}
        </Button>
      </form>

      {state.status === "loading" ? (
        <div
          className={`${superAdminTableWorkspaceClass} h-40 animate-pulse`}
          data-testid="super-admin-users-loading"
          aria-busy="true"
        />
      ) : null}

      {state.status === "error" ? (
        <ProductErrorState
          testId="super-admin-users-error"
          title={t("superAdmin.users.loadErrorTitle")}
          description={t("superAdmin.users.loadErrorBody")}
          onRetry={onRetry}
          retryLabel={t("superAdmin.retry")}
        />
      ) : null}

      {ready && items.length === 0 ? (
        <p className="m-0 text-sm text-muted" data-testid="super-admin-users-empty">
          {t("superAdmin.users.empty")}
        </p>
      ) : null}

      {ready && items.length > 0 ? (
        <div className={superAdminTableWorkspaceClass}>
          <div className={superAdminTableScrollClass}>
            <table className={`${superAdminTableClass} min-w-[40rem]`} data-testid="super-admin-users-table">
              <thead>
                <tr className={superAdminTableHeadRowClass}>
                  <HeaderCell>{t("superAdmin.users.columns.user")}</HeaderCell>
                  <HeaderCell>{t("superAdmin.users.columns.status")}</HeaderCell>
                  <HeaderCell>{t("superAdmin.users.columns.organizations")}</HeaderCell>
                  <HeaderCell>{t("superAdmin.users.columns.created")}</HeaderCell>
                </tr>
              </thead>
              <tbody>
                {items.map((user) => (
                  <tr
                    key={user.id}
                    className={superAdminTableBodyRowClass}
                    data-testid={`super-admin-user-row-${user.id}`}
                  >
                    <td className={superAdminTableBodyCellClass}>
                      <Link
                        to={`/super-admin/users/${user.id}`}
                        className="font-medium text-foreground no-underline hover:underline"
                        data-testid={`super-admin-user-link-${user.id}`}
                      >
                        {user.displayName}
                      </Link>
                      <span className="block text-[0.75rem] text-muted">
                        <LtrValue>{user.email}</LtrValue>
                      </span>
                    </td>
                    <td className={superAdminTableBodyCellClass}>
                      <SuperAdminUserStatusBadge status={user.status} />
                    </td>
                    <td className={superAdminTableBodyCellClass}>
                      <LtrValue>{numberFormat.format(user.organizationCount)}</LtrValue>
                    </td>
                    <td className={superAdminTableBodyCellClass}>
                      <LtrValue>{new Date(user.createdAt).toLocaleDateString(i18n.language)}</LtrValue>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <SuperAdminCursorPaginationBar
            pageSize={pageSize}
            onPageSizeChange={onPageSizeChange}
            hasNextPage={nextCursor !== null}
            isFirstPage={isFirstPage}
            onNextPage={onNextPage}
            onFirstPage={onFirstPage}
            pageSizeLabel={t("superAdmin.pagination.pageSize")}
            nextLabel={t("superAdmin.organizations.next")}
            firstLabel={t("superAdmin.pagination.first")}
            showingCountLabel={t("superAdmin.pagination.showingCount")}
            itemCount={items.length}
            testIdPrefix="super-admin-users"
          />
        </div>
      ) : null}
    </div>
  );
}
